import { motion } from 'motion/react';
import { memo } from 'react';
import { MGSearchResultCard } from '@/features/GlobalSearch/components/search/MGSearchResultCard';
import { SearchButton } from '@/features/GlobalSearch/components/search/SearchButton';
import { SectionHeading } from '@/features/GlobalSearch/components/trending/SectionHeading';
import useTWScreens from '@/hooks/ui/useTWScreens';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { MotionGraphics, SearchResponseHit } from '@music-vine/setlist';

// Keep this list in sync with `LUT_ZONE_LINKS` in
// features/LUTsZones/zone-links — LUTs zones drive both the /luts nav and
// the search "Popular searches" chips (per the DEV-5883 AC).
export const LUT_POPULAR_SEARCHES = [{
  slug: 'creator-luts',
  name: 'Creator',
  href: '/luts/category/creator-luts'
}, {
  slug: 'cinematic-luts',
  name: 'Cinematic',
  href: '/luts/category/cinematic-luts'
}, {
  slug: 'camera-luts',
  name: 'Camera',
  href: '/luts/category/camera-luts'
}, {
  slug: 'movie-luts',
  name: 'Movie',
  href: '/luts/category/movie-luts'
}, {
  slug: 'professional-luts',
  name: 'Professional',
  href: '/luts/category/professional-luts'
}, {
  slug: 'occasion-luts',
  name: 'Occasion',
  href: '/luts/category/occasion-luts'
}, {
  slug: 'creative-luts',
  name: 'Creative',
  href: '/luts/category/creative-luts'
}];
interface LutsTrendingSectionProps {
  closePopup: () => void;
  onNameClick: (content: string | undefined, endSlug: string | undefined, assetType: AssetType | 'general') => void;
  trackTrendingClick: (endSlug: string) => void;
  trendingLutsAssets?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
}
export const LutsTrendingSection = memo(({
  trendingLutsAssets,
  closePopup,
  trackTrendingClick,
  onNameClick
}: LutsTrendingSectionProps) => {
  const {
    isMd
  } = useTWScreens();
  const assetsData = trendingLutsAssets?.hits ?? [];
  const hasAssets = assetsData.length > 0;
  const AnimatedContainer = isMd ? motion.div : 'div';
  const AnimatedItem = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} initial={isMd ? {
    opacity: 0
  } : undefined} style={{
    position: 'relative'
  }} transition={isMd ? {
    duration: 0.15
  } : undefined}>
        <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
          <div className="mb-2">
            <SectionHeading>Popular searches</SectionHeading>
          </div>

          <AnimatedItem className="flex flex-wrap gap-2">
            {LUT_POPULAR_SEARCHES.map(zone => <AnimatedItem animate={isMd ? {
          opacity: 1,
          scale: 1
        } : undefined} initial={isMd ? {
          opacity: 0,
          scale: 0.95
        } : undefined} key={zone.slug} transition={isMd ? {
          duration: 0.2,
          delay: 0
        } : undefined}>
                <SearchButton assetType="luts" closePopup={closePopup} href={zone.href} id={zone.slug} name={zone.name.toLowerCase()} onNameClick={onNameClick} testId="popular-luts-search" />
              </AnimatedItem>)}
          </AnimatedItem>
        </div>

        {hasAssets && <div className="main-search-results my-4 px-4 md:my-0 md:mb-0 md:px-0">
            <div className="mb-2">
              <SectionHeading>LUTs</SectionHeading>
            </div>
            <AnimatedItem className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {assetsData.slice(0, 4).map((hit, idx) => {
          const lut = hit.document as unknown as MotionGraphics;
          return <AnimatedItem animate={isMd ? {
            opacity: 1
          } : undefined} initial={isMd ? {
            opacity: 0
          } : undefined} key={`lut-asset-${lut.id}`} transition={isMd ? {
            duration: 0.2,
            delay: Math.min(idx * 0.05, 0.25)
          } : undefined}>
                    <MGSearchResultCard compatibility={lut.compatibility} contributor={lut.contributor.name} isLut isNew={false} name={lut.name} onClick={(slug: string) => {
              closePopup();
              trackTrendingClick(`/luts/asset/${slug}`);
            }} posterUrl={lut.image} previewVideoUrl={lut.preview_video_url} slug={lut.slug} variant="globalSearch" />
                  </AnimatedItem>;
        })}
            </AnimatedItem>
          </div>}
      </AnimatedContainer>;
});
LutsTrendingSection.displayName = 'LutsTrendingSection';